import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import clsx from "clsx";
import {
  useCalendarDndStore,
  type DateRangePreview,
} from "../../store/calendarDndStore";
import { addDaysToDate } from "../../utils/zonedTime";
import type { RangeEdge } from "../../utils/calendarRangeResize";
import { Button } from "../Button";

const AUTO_NAVIGATION_LOCK_MS = 420;

export interface DateRangeResizeTarget {
  date: string;
  zone: string;
}

interface DateRangeResizeHandleProps {
  edge: RangeEdge;
  label: string;
  currentDate: string;
  zoneForDate: (date: string) => string;
  previewForTarget: (target: DateRangeResizeTarget) => DateRangePreview;
  commitTarget: (target: DateRangeResizeTarget) => void;
  onAutoNavigate?: (direction: -1 | 1) => void;
  autoNavigateRootSelector?: string;
}

/** Date-only resize handle shared by all-day notes and month ranges. */
export function DateRangeResizeHandle({
  edge,
  label,
  currentDate,
  zoneForDate,
  previewForTarget,
  commitTarget,
  onAutoNavigate,
  autoNavigateRootSelector,
}: DateRangeResizeHandleProps) {
  const setDateRangePreview = useCalendarDndStore(
    (state) => state.setDateRangePreview,
  );
  const autoNavigateAt = useRef(0);

  const targetAtPoint = (clientX: number, clientY: number) => {
    const target = document
      .elementsFromPoint(clientX, clientY)
      .map((element) =>
        element.closest<HTMLElement>("[data-calendar-date-target]"),
      )
      .find((element): element is HTMLElement => Boolean(element));
    const date = target?.dataset.calendarDateTarget;
    const zone = target?.dataset.calendarZone;
    return date && zone ? { date, zone } : null;
  };

  const maybeAutoNavigate = (clientY: number) => {
    if (!onAutoNavigate || !autoNavigateRootSelector) return;
    const calendar = document.querySelector<HTMLElement>(
      autoNavigateRootSelector,
    );
    if (!calendar) return;
    const bounds = calendar.getBoundingClientRect();
    const direction =
      clientY < bounds.top + 36 ? -1 : clientY > bounds.bottom - 36 ? 1 : 0;
    if (
      !direction ||
      Date.now() - autoNavigateAt.current < AUTO_NAVIGATION_LOCK_MS
    )
      return;
    autoNavigateAt.current = Date.now();
    onAutoNavigate(direction as -1 | 1);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    let target: DateRangeResizeTarget | null = null;
    const handle = event.currentTarget;
    const pointerId = event.pointerId;

    const cleanup = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
      if (handle.hasPointerCapture(pointerId))
        handle.releasePointerCapture(pointerId);
    };
    const handleMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      target = targetAtPoint(moveEvent.clientX, moveEvent.clientY);
      if (target) setDateRangePreview(previewForTarget(target));
      else maybeAutoNavigate(moveEvent.clientY);
    };
    const handleUp = () => {
      cleanup();
      const finalTarget = target;
      setDateRangePreview(null);
      if (finalTarget) commitTarget(finalTarget);
    };
    const handleCancel = () => {
      cleanup();
      setDateRangePreview(null);
    };
    window.addEventListener("pointermove", handleMove, { passive: false });
    window.addEventListener("pointerup", handleUp, { once: true });
    window.addEventListener("pointercancel", handleCancel, { once: true });
  };

  return (
    <Button
      variant="unstyled"
      className={clsx(
        "absolute inset-y-0 z-10 w-4 cursor-ew-resize touch-none rounded-sm bg-current opacity-20 transition-opacity hover:opacity-60 focus:opacity-60 focus:outline-none group-hover/range:opacity-45 sm:w-2",
        edge === "start" ? "left-0" : "right-0",
      )}
      aria-label={`Resize ${label} ${edge} date`}
      title={`Drag or use arrow keys to change the ${edge} date`}
      data-range-edge={edge}
      onPointerDown={handlePointerDown}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        event.stopPropagation();
        const amount =
          (event.key === "ArrowLeft" ? -1 : 1) * (event.shiftKey ? 7 : 1);
        const date = addDaysToDate(currentDate, amount);
        const target = { date, zone: zoneForDate(date) };
        const preview = previewForTarget(target);
        setDateRangePreview(preview);
        window.setTimeout(() => setDateRangePreview(null), 500);
        if (!preview.invalid) commitTarget(target);
      }}
    />
  );
}
